import React, { useState, useRef, useCallback } from 'react';
import { View, StyleSheet, KeyboardAvoidingView, Platform } from 'react-native';
import { TextInput, Button, Text, useTheme, Surface } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { InfoBox } from '../../src/components/InfoBox';
import { confirmPhoneVerificationCode } from '../../src/features/phoneVerification/phoneVerificationService';
import type { ApiResponse } from '../../src/api/apiService';
import { useCompanyContext } from '../../src/context/CompanyContext';

export default function PhoneVerificationConfirmScreen() {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const { refreshCompanies } = useCompanyContext();
  const params = useLocalSearchParams<{ phone?: string; cooldown?: string }>();
  
  const phoneNumber = params.phone || '';
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(parseInt(params.cooldown || '0', 10));
  const [attempts, setAttempts] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  
  const otpInputRef = useRef<React.ComponentRef<typeof TextInput> | null>(null);
  const otpLength = 6;

  const formatOTP = (value: string): string => {
    return value.replace(/\D/g, '').slice(0, otpLength);
  };

  const handleOTPChange = (text: string) => {
    const formatted = formatOTP(text);
    setOtp(formatted);
    setError(null);
  };

  // Resend cooldown timer
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleVerify = useCallback(async () => {
    if (otp.length !== otpLength) {
      setError(t('enterFullCode', 'Please enter the complete {{length}}-digit code', { length: otpLength }));
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      const response: ApiResponse<{ message?: string; phone_number?: string }> = 
        await confirmPhoneVerificationCode(phoneNumber, otp);

      if (response.ok) {
        setIsVerified(true);
        // Refresh company/user data to update phone_verified status globally
        refreshCompanies();
        // Show success and navigate to dashboard with refresh flag
        setTimeout(() => {
          router.replace({ pathname: '/dashboard/home', params: { phone_verified: '1' } });
        }, 1500);
      } else {
        const responseData = response.data as any;
        const errorDetail = responseData?.detail || 
                           responseData?.non_field_errors?.[0] ||
                           responseData?.code?.[0] ||
                           t('verificationFailed', 'Verification failed. Please check the code and try again.');
        setError(errorDetail);
        setAttempts((prev) => prev + 1);
        setOtp('');
        otpInputRef.current?.focus();
      }
    } catch (err) {
      setError(t('verificationError', 'An error occurred. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  }, [otp, otpLength, phoneNumber, t, router]);

  const handleResend = useCallback(() => {
    // Go back to request screen
    router.replace({
      pathname: '/dashboard/phone-verification-request',
      params: { phone: phoneNumber }
    });
  }, [phoneNumber, router]);

  // Mask phone number for display
  const maskedPhone = phoneNumber.length > 4 
    ? `***${phoneNumber.slice(-4)}` 
    : `***${phoneNumber}`;

  if (isVerified) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.successContainer}>
          <Surface style={styles.content} elevation={1}>
            <Text variant="headlineSmall" style={styles.successTitle}>
              {t('phoneVerified', 'Phone Verified!')}
            </Text>
            <Text variant="bodyMedium" style={styles.successText}>
              {t('phoneVerifiedMessage', 'Your phone number has been successfully verified.')}
            </Text>
          </Surface>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <Surface style={styles.content} elevation={1}>
          <Text variant="headlineSmall" style={styles.title}>
            {t('enterVerificationCode', 'Enter Verification Code')}
          </Text>
          
          <Text variant="bodyMedium" style={styles.description}>
            {t('codeSentToPhone', 'We sent a code to {{phone}}', { phone: maskedPhone })}
          </Text>

          <View style={styles.inputContainer}>
            <TextInput
              ref={otpInputRef}
              label={t('verificationCode', 'Verification Code')}
              mode="outlined"
              value={otp}
              onChangeText={handleOTPChange}
              keyboardType="number-pad"
              textContentType="oneTimeCode"
              autoComplete="one-time-code"
              disabled={isLoading}
              error={!!error}
              style={styles.otpInput}
              maxLength={otpLength}
            />
            
            {error && (
              <Text variant="bodySmall" style={[styles.errorText, { color: theme.colors.error }]}>
                {error}
              </Text>
            )}
          </View>

          <Button
            mode="contained"
            onPress={handleVerify}
            loading={isLoading}
            disabled={isLoading || otp.length !== otpLength}
            style={styles.button}
          >
            {t('verify', 'Verify')}
          </Button>

          <View style={styles.resendContainer}>
            <Text variant="bodySmall" style={styles.resendText}>
              {t('didNotReceiveCode', "Didn't receive the code?")}
            </Text>
            <Button
              mode="text"
              compact
              onPress={handleResend}
              disabled={resendCooldown > 0}
            >
              {resendCooldown > 0 
                ? t('resendIn', 'Resend in {{seconds}}s', { seconds: resendCooldown })
                : t('resend', 'Resend')}
            </Button>
          </View>

          {attempts >= 3 && (
            <InfoBox
              variant="warning"
              title={t('havingTrouble', 'Having trouble?')}
              message={t('checkPhoneNumber', 'Make sure your phone number is correct and can receive SMS messages.')}
            />
          )}

          <Button
            mode="text"
            onPress={() => router.back()}
            style={styles.backButton}
          >
            {t('goBack', 'Go Back')}
          </Button>
        </Surface>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardView: {
    flex: 1,
  },
  successContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  successTitle: {
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
    color: '#2E7D32',
  },
  successText: {
    textAlign: 'center',
    opacity: 0.7,
  },
  content: {
    margin: 16,
    padding: 20,
    borderRadius: 12,
  },
  title: {
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  description: {
    textAlign: 'center',
    marginBottom: 24,
    opacity: 0.7,
  },
  inputContainer: {
    marginBottom: 16,
  },
  otpInput: {
    textAlign: 'center',
    fontSize: 24,
    letterSpacing: 8,
  },
  errorText: {
    marginTop: 4,
    marginLeft: 8,
    textAlign: 'center',
  },
  button: {
    marginTop: 8,
  },
  resendContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  },
  resendText: {
    marginRight: 4,
    opacity: 0.7,
  },
  backButton: {
    marginTop: 12,
  },
});
